import React, { useEffect, useState } from 'react';
import { Home, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../../lib/adminApi';

/**
 * Sub-section that renders the TAH (Therapy @Home) template editor.
 * Embedded inside the Templates tab in AdminHub — sits below the Facility Overview editor.
 */
export const TahTemplateSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.tahList();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load TAH template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await adminApi.tahCreate(newName.trim());
      setNewName('');
      toast.success('TAH item added');
      load();
    } catch (err) {
      toast.error(err.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const rename = async (item, newValue) => {
    if (newValue === item.name) return;
    setSavingId(item.id);
    try {
      await adminApi.tahUpdate(item.id, { name: newValue });
      load();
    } catch (err) {
      toast.error(err.message || 'Rename failed');
    } finally {
      setSavingId('');
    }
  };

  const remove = async (item) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove TAH item "${item.name}"?`)) return;
    setDeletingId(item.id);
    try {
      await adminApi.tahDelete(item.id);
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div data-testid="tah-template-section" className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            <Home className="w-4 h-4 text-cyan-600" /> TAH (Therapy @Home) Template
          </div>
          <div className="text-xs text-slate-500">Items shown in the Therapy @Home selector on every tactical.</div>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <form onSubmit={create} className="flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New TAH item name…"
          data-testid="tah-new-name"
          className="flex-1 px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-cyan-400"
        />
        <button type="submit" disabled={creating || !newName.trim()} data-testid="tah-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> {creating ? 'Adding…' : 'Add Item'}
        </button>
      </form>

      <div className="space-y-2">
        {loading && <div className="text-sm text-slate-500 py-4">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 py-6 text-center text-sm text-slate-500">
            No TAH items yet. Add the first above.
          </div>
        )}
        {!loading && items.map((item) => (
          <TahRow
            key={item.id}
            item={item}
            isSaving={savingId === item.id}
            isDeleting={deletingId === item.id}
            onRename={rename}
            onRemove={remove}
          />
        ))}
      </div>
    </div>
  );
};

const TahRow = ({ item, isSaving, isDeleting, onRename, onRemove }) => {
  const [value, setValue] = useState(item.name);
  const dirty = value !== item.name;
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`tah-row-${item.id}`}>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1 px-2 py-1 text-sm rounded border border-transparent focus:outline-none focus:border-cyan-300 bg-transparent"
      />
      {dirty && (
        <button
          onClick={() => onRename(item, value.trim())}
          disabled={isSaving || !value.trim()}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
        >
          <Save className="w-3 h-3" /> {isSaving ? 'Saving…' : 'Save'}
        </button>
      )}
      <button
        onClick={() => onRemove(item)}
        disabled={isDeleting}
        title="Remove"
        className="text-slate-400 hover:text-rose-600 p-1 disabled:opacity-50"
        data-testid={`tah-delete-${item.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};
